import React, { RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useSelector } from '../../../../util/store/store';
import { OPERATION_TYPE } from '../../../../util/varibles/constants';
import { selectPlan } from '../../constants';
import styles from './style.module.scss';

interface IProps {
    calendarRef: RefObject<HTMLDivElement>
}

interface IRelationLine {
    id: string
    path: string
    startX: number
    startY: number
    endX: number
    endY: number
}

const InvoiceRelationLines: React.FC<IProps> = ({ calendarRef }) => {
    const operations = useSelector(selectPlan.ops);
    const [hoveredId, setHoveredId] = useState('');
    const [lines, setLines] = useState<IRelationLine[]>([]);
    const [size, setSize] = useState({ width: 0, height: 0 });

    const updateLines = useCallback(() => {
        const calendar = calendarRef.current;
        const hoveredOperation = operations[hoveredId];
        if (!calendar || !hoveredOperation) {
            setLines([]);
            return;
        }

        const isEvent = hoveredOperation.operation.operation_type === OPERATION_TYPE.EVENT;
        const mainOperations = isEvent
            ? Object.values(operations).filter(item => item.operation.operation_type !== OPERATION_TYPE.EVENT
                && item.operation.invoices?.some(invoice => invoice.operation_type === OPERATION_TYPE.EVENT
                    && invoice.operation_id === hoveredId))
            : [hoveredOperation];
        const calendarRect = calendar.getBoundingClientRect();
        const toPoint = (element: HTMLElement) => {
            const rect = element.getBoundingClientRect();
            return {
                left: rect.left - calendarRect.left + calendar.scrollLeft,
                top: rect.top - calendarRect.top + calendar.scrollTop,
                right: rect.right - calendarRect.left + calendar.scrollLeft,
                centerY: rect.top - calendarRect.top + calendar.scrollTop + rect.height / 2,
            };
        };
        const relations = mainOperations.flatMap(item => (item.operation.invoices || [])
            .filter(invoice => invoice.operation_type === OPERATION_TYPE.EVENT
                && (!isEvent || invoice.operation_id === hoveredId))
            .map(invoice => ({ mainId: item.operation.id, eventId: invoice.operation_id })));

        const nextLines = relations.reduce<IRelationLine[]>((result, { mainId, eventId }) => {
            const mainElement = document.getElementById(mainId);
            const eventElement = document.getElementById(eventId);
            if (!mainElement || !calendar.contains(mainElement)
                || !eventElement || !calendar.contains(eventElement))
                return result;

            const mainPoint = toPoint(mainElement);
            const eventPoint = toPoint(eventElement);
            const startX = mainPoint.left;
            const startY = mainPoint.centerY;
            const endX = eventPoint.left;
            const endY = eventPoint.centerY;
            const railX = Math.max(2, Math.min(startX, endX) - 7);

            result.push({
                id: `${mainId}-${eventId}`,
                path: `M ${startX} ${startY} H ${railX} V ${endY} H ${endX}`,
                startX,
                startY,
                endX,
                endY,
            });
            return result;
        }, []);

        setSize({ width: calendar.scrollWidth, height: calendar.scrollHeight });
        setLines(nextLines);
    }, [calendarRef, hoveredId, operations]);

    useEffect(() => {
        const calendar = calendarRef.current;
        if (!calendar)
            return;

        const handlePointerMove = (event: PointerEvent) => {
            if (event.buttons !== 0 || calendar.dataset.modeDrag) {
                setHoveredId('');
                return;
            }

            const element = (event.target as HTMLElement).closest<HTMLElement>('[data-rule="operation"]');
            if (!element || !calendar.contains(element)) {
                setHoveredId('');
                return;
            }

            const id = element.dataset.operationId || '';
            const operation = operations[id]?.operation;
            setHoveredId(operation ? id : '');
        };
        const clearLines = () => setHoveredId('');

        calendar.addEventListener('pointerdown', clearLines);
        calendar.addEventListener('pointermove', handlePointerMove);
        calendar.addEventListener('pointerleave', clearLines);
        return () => {
            calendar.removeEventListener('pointerdown', clearLines);
            calendar.removeEventListener('pointermove', handlePointerMove);
            calendar.removeEventListener('pointerleave', clearLines);
        };
    }, [calendarRef, operations]);

    useLayoutEffect(() => {
        updateLines();
        const calendar = calendarRef.current;
        if (!calendar || !hoveredId)
            return;

        const observer = typeof ResizeObserver === 'undefined'
            ? undefined
            : new ResizeObserver(updateLines);
        observer?.observe(calendar);
        calendar.querySelectorAll<HTMLElement>('[data-rule="operation"]').forEach(element => {
            observer?.observe(element);
        });
        window.addEventListener('resize', updateLines);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateLines);
        };
    }, [calendarRef, hoveredId, updateLines]);

    if (lines.length === 0)
        return null;

    return <><svg
        className={styles.invoiceRelations}
        width={size.width}
        height={size.height}
        aria-hidden='true'
    >
        {lines.map(line => <path key={line.id} className={styles.invoiceRelationLine} d={line.path} />)}
    </svg>
    <svg
        className={`${styles.invoiceRelations} ${styles.invoiceRelationPoints}`}
        width={size.width}
        height={size.height}
        aria-hidden='true'
    >
        {lines.map(line => <g key={line.id}>
            <circle className={styles.invoiceRelationPoint} cx={line.startX} cy={line.startY} r='3' />
            <circle className={styles.invoiceRelationPoint} cx={line.endX} cy={line.endY} r='3' />
        </g>)}
    </svg></>;
};

export default InvoiceRelationLines;
